/* eslint-disable prettier/prettier */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useAuth from '@modules/auth/hooks/api/useAuth';

interface GoogleCalendarFAQProps {}
const GoogleCalendarFAQ = (_props: GoogleCalendarFAQProps) => {
  const { user } = useAuth();

  if (!user) {
    return null;
  }
  return (
    <Stack direction="column" gap={1} sx={{ width: '100%' }}>
      <Accordion
        square={false}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          margin: 0,
          '&.MuiPaper-root': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
            C'est quoi Google Agenda, et pourquoi je dois l'utiliser ?
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" sx={{ mb: 2 }}>
            Google Agenda est simplement l'outil qui permet à AlloClients de{' '}
            <strong>savoir quand vous êtes disponible.</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
            Son seul rôle :
            <br />
            Éviter que des personnes essaient de prendre rendez-vous sur des créneaux où vous êtes
            déjà occupé.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Concrètement
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vous continuez à gérer vos rendez-vous <strong>exactement comme aujourd'hui</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • AlloClients <strong>ne modifie rien</strong> dans votre agenda
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Vos nouveaux rendez-vous sont ajoutés <strong>automatiquement.</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            AlloClients se contente de <strong>vérifier vos disponibilités</strong> pour ne vous
            envoyer que des demandes compatibles.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Confidentialité
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vos rendez-vous restent <strong>strictement privés</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • AlloClients ne voit <strong>ni les détails</strong>, ni les noms, ni le contenu
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Aucune donnée n'est partagée avec qui que ce soit
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Important à savoir
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • La connexion est <strong>gratuite</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Cela prend <strong>environ 30 secondes</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Vous pouvez <strong>déconnecter l'agenda à tout moment</strong> depuis votre compte
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            Si vous avez un doute ou besoin d'aide à cette étape :
          </Typography>
          <Link href={`mailto:${process.env.NEXT_PUBLIC_SUPPORT_EMAIL}`}>
            {process.env.NEXT_PUBLIC_SUPPORT_EMAIL}
          </Link>
        </AccordionDetails>
      </Accordion>
      <Accordion
        square={false}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          margin: 0,
          // paper root
          '&.MuiPaper-root': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
            Je ne sais pas comment faire
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" sx={{ mb: 3 }}>
            Pas d'inquiétude.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Cliquez simplement sur « <strong>Connecter mon agenda Google</strong> », puis validez
            les écrans qui s'affichent.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
            Tout se fait automatiquement et prend <strong>moins d'une minute.</strong>
            <br />
            Vous n'avez rien à configurer.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Si vous n'avez pas encore d'agenda Google, il sera créé automatiquement, sans action de
            votre part.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Ce que ça change pour vous
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vous continuez à gérer vos rendez-vous <strong>exactement comme aujourd'hui</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • AlloClients <strong>ne modifie rien</strong> dans votre agenda
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Aucun conflit avec vos outils existants
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            C'est uniquement une connexion technique pour que le service fonctionne correctement,
            <strong>sans impact sur vos habitudes.</strong>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion
        square={false}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          margin: 0,
          // paper root
          '&.MuiPaper-root': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
            J'ai peur que mes outils existants soient perturbés
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" sx={{ mb: 3 }}>
            C'est une crainte normale.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            La connexion de l'agenda est conçue pour{' '}
            <strong>ne rien changer à votre organisation actuelle.</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
            Vous continuez à utiliser vos outils exactement comme aujourd'hui.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Concrètement
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • AlloClients <strong>ne modifie aucun rendez-vous</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vos nouveaux rendez-vous sont ajoutés <strong>automatiquement.</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Aucun conflit avec vos autres outils ou systèmes de réservation
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            L'agenda sert uniquement à{' '}
            <strong>éviter que des demandes arrivent sur des créneaux déjà pris.</strong>
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Vous gardez le contrôle
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • La connexion est <strong>gratuite</strong> et prend{' '}
            <strong>environ 30 secondes</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vous pouvez <strong>déconnecter l'agenda à tout moment</strong> depuis votre compte
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Cela remet immédiatement la situation dans son état d'origine
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Confidentialité
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vos rendez-vous restent <strong>strictement privés</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Aucun détail, aucun nom, aucun contenu n'est visible
          </Typography>

          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            Si vous avez la moindre question ou souhaitez être accompagné à cette étape :
          </Typography>
          <Link href={`mailto:${process.env.NEXT_PUBLIC_SUPPORT_EMAIL}`}>
            {process.env.NEXT_PUBLIC_SUPPORT_EMAIL}
          </Link>
        </AccordionDetails>
      </Accordion>
      <Accordion
        square={false}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          margin: 0,
          // paper root
          '&.MuiPaper-root': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
            Je n'ai pas d'agenda Google
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" sx={{ mb: 2 }}>
            Aucun problème.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Si vous n'avez pas d'agenda Google, <strong>ce n'est pas bloquant.</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
            Cliquez simplement sur « <strong>Connecter mon agenda Google</strong> » et suivez les
            étapes : tout se fait automatiquement, sans configuration manuelle.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Important à savoir
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vous continuez à gérer vos rendez-vous <strong>exactement comme aujourd'hui</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • AlloClients <strong>ne modifie rien</strong> dans votre organisation
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Aucun conflit avec vos outils existants
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            L'agenda sert uniquement à <strong>indiquer vos disponibilités</strong>, afin d'éviter
            les demandes sur des créneaux déjà pris.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Et si vous n'avez pas de compte Google ?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Un compte Google est simplement nécessaire pour cette connexion.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Si vous n'en avez pas encore, <strong>il peut être créé en quelques minutes</strong>, et
            nous pouvons vous guider si besoin.
          </Typography>

          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            Si vous avez une question ou souhaitez être accompagné à cette étape :
          </Typography>
          <Link href={`mailto:${process.env.NEXT_PUBLIC_SUPPORT_EMAIL}`}>
            {process.env.NEXT_PUBLIC_SUPPORT_EMAIL}
          </Link>
        </AccordionDetails>
      </Accordion>
      <Accordion
        square={false}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          margin: 0,
          // paper root
          '&.MuiPaper-root': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
            Je ne veux pas partager mes informations privées
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" sx={{ mb: 3 }}>
            C'est tout à fait compréhensible.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
            La connexion de l'agenda <strong>ne donne accès à aucune information privée.</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            AlloClients ne voit{' '}
            <strong>ni vos rendez-vous, ni les noms, ni les clients, ni le contenu.</strong>
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Ce que fait réellement la connexion
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Elle indique uniquement <strong>si vous êtes disponible ou non</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Aucun rendez-vous n'est lu en détail
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Rien n'est modifié, vos nouveaux rendez-vous sont ajoutés{' '}
            <strong>automatiquement.</strong>
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Vous gardez le contrôle
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • La connexion est <strong>gratuite</strong> et prend{' '}
            <strong>environ 30 secondes</strong>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Vous pouvez <strong>déconnecter l'agenda à tout moment</strong> depuis votre compte
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • Cela remet immédiatement la situation dans son état d'origine
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
            Votre organisation reste exactement la même :
            <br />
            AlloClients s'adapte à votre agenda, <strong>pas l'inverse.</strong>
          </Typography>

          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            Si vous avez une question ou souhaitez être accompagné à cette étape :
          </Typography>
          <Link href={`mailto:${process.env.NEXT_PUBLIC_SUPPORT_EMAIL}`}>
            {process.env.NEXT_PUBLIC_SUPPORT_EMAIL}
          </Link>
        </AccordionDetails>
      </Accordion>
      <Accordion
        square={false}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          margin: 0,
          // paper root
          '&.MuiPaper-root': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
            Comment je peux créer un compte Google ?
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" sx={{ mb: 2 }}>
            Pour créer un compte Google :
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            1. Allez sur{' '}
            <Link href="https://accounts.google.com/signup" target="_blank" rel="noopener">
              https://accounts.google.com/signup
            </Link>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            2. Remplissez prénom, nom, identifiant et mot de passe
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            3. Validez la création (Google peut demander un code SMS)
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            4. Votre compte Google est prêt à être utilisé avec Google Agenda
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '0.95rem' }}>
            Notes importantes
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
            • Cette étape ne modifie <strong>aucun rendez-vous</strong> existant.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, pl: 3 }}>
            • AlloClients <strong>ne modifie rien</strong> dans votre agenda.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default GoogleCalendarFAQ;
